/**
 * FolderTree Component
 * 
 * Displays folders with drag-drop support for organizing conversations.
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import '../styles/FolderTree.css';

export default function FolderTree({ 
  conversations, 
  selectedId, 
  onSelect,
  onMoveToFolder,
  onRenameConversation,
  onPinConversation,
  onArchiveConversation,
  onFolderSelect
}) {
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(() => {
    // Restore collapsed state from localStorage
    try {
      const saved = localStorage.getItem('tim_folder_collapsed');
      if (saved) {
        return new Set(JSON.parse(saved));
      }
    } catch (e) {
      console.debug('Failed to restore folder state:', e);
    }
    return new Set();
  });
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null); // Track which conversation's menu is open

  // Section collapse state — only list sections that default to collapsed
  const [sectionExpanded, setSectionExpanded] = useState({ Older: false, archived: false });
  const isSectionExpanded = (key) => sectionExpanded[key] !== false;
  const toggleSection = (key) => {
    setSectionExpanded(prev => ({ ...prev, [key]: !isSectionExpanded(key) }));
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  // Re-fetch folders when conversations change (for count updates)
  useEffect(() => {
    fetchFolders();
  }, [conversations]);

  // Persist expanded state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('tim_folder_collapsed', JSON.stringify([...expandedFolders]));
    } catch (e) {
      console.debug('Failed to save folder state:', e);
    }
  }, [expandedFolders]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  const fetchFolders = async () => {
    try {
      const response = await fetch('/folders', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFolders(data.folders);
        // No auto-expand - respect user's persisted state from localStorage
        // First-time users see all folders collapsed
      }
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      const response = await fetch('/folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newFolderName.trim() })
      });
      
      if (response.ok) {
        setNewFolderName('');
        setShowNewFolder(false);
        fetchFolders();
      }
    } catch (error) {
      console.error('Failed to create folder:', error);
    }
  };

  const deleteFolder = async (e, folderId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this folder? Conversations will be moved to unfiled.')) {
      return;
    }
    
    try {
      const response = await fetch(`/folders/${folderId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        fetchFolders();
      }
    } catch (error) {
      console.error('Failed to delete folder:', error);
    }
  };

  const toggleFolder = (folderId) => {
    const folderKey = String(folderId);
    const folder = folders.find(f => f.id === folderId);
    const folderName = folder?.name || null;
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderKey)) {
        next.delete(folderKey);
        // Folder collapsed - clear folder selection
        if (onFolderSelect) onFolderSelect(null, null);
      } else {
        next.add(folderKey);
        // Folder expanded - select it
        if (onFolderSelect) onFolderSelect(folderId, folderName);
      }
      return next;
    });
  };

  const handleDragStart = (e, conversationId) => {
    e.dataTransfer.setData('conversationId', String(conversationId));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderId);
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleDrop = async (e, folderId) => {
    e.preventDefault();
    setDragOverFolder(null);
    
    const conversationId = e.dataTransfer.getData('conversationId');
    if (conversationId && onMoveToFolder) {
      await onMoveToFolder(parseInt(conversationId), folderId);
      fetchFolders();
    }
  };

  const handleRename = async (conversationId, newTitle) => {
    if (onRenameConversation) {
      await onRenameConversation(conversationId, newTitle);
    }
  };

  const toggleMenu = (conversationId, e) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === conversationId ? null : conversationId);
  };

  // Extract archived first, then pinned, then group the rest
  const archived = [];
  const nonArchived = [];
  
  conversations.forEach(conv => {
    if (conv.archived) {
      archived.push(conv);
    } else {
      nonArchived.push(conv);
    }
  });

  const pinned = [];
  const unpinned = [];
  
  nonArchived.forEach(conv => {
    if (conv.pinned) {
      pinned.push(conv);
    } else {
      unpinned.push(conv);
    }
  });

  // Group unpinned conversations by folder
  // IMPORTANT: Both folder.id and conv.folder_id should be strings from the API
  const conversationsByFolder = {};
  const unfiled = [];
  
  unpinned.forEach(conv => {
    // conv.folder_id comes as string from API (or null)
    if (conv.folder_id) {
      const folderKey = String(conv.folder_id);
      if (!conversationsByFolder[folderKey]) {
        conversationsByFolder[folderKey] = [];
      }
      conversationsByFolder[folderKey].push(conv);
    } else {
      unfiled.push(conv);
    }
  });

  // Group unfiled by date
  const groupedUnfiled = groupByDate(unfiled);

  return (
    <div className="folder-tree">
      {/* Folders Section - HIDDEN: file architecture bug, re-enable post-launch */}
      {false && (
	<div className="folder-section">
        <div className="folder-header">
          <span>Folders</span>
          <button 
            className="folder-add-btn"
            onClick={() => setShowNewFolder(true)}
            title="New folder"
          >
            +
          </button>
        </div>

        {showNewFolder && (
          <div className="folder-new-input">
            <input
              type="text"
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createFolder()}
              placeholder="Folder name"
              autoFocus
            />
            <button onClick={createFolder}>✓</button>
            <button onClick={() => setShowNewFolder(false)}>×</button>
          </div>
        )}

        {folders.map(folder => {
          // Use string key consistently for both expanded state and conversation lookup
          const folderKey = String(folder.id);
          const folderConversations = conversationsByFolder[folderKey] || [];
          const isExpanded = expandedFolders.has(folderKey);
          
          return (
            <div key={folder.id} className="folder-item">
              <div 
                className={`folder-row ${dragOverFolder === folder.id ? 'drag-over' : ''}`}
                onClick={() => toggleFolder(folder.id)}
                onDragOver={e => handleDragOver(e, folder.id)}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, folder.id)}
              >
                <span className="folder-chevron">
                  {isExpanded ? '▼' : '▶'}
                </span>
                <span className={`folder-icon ${isExpanded ? 'expanded' : ''}`}>
                  {isExpanded ? '📂' : '📁'}
                </span>
                <span 
                  className="folder-name"
                  style={{ color: folder.color || 'inherit' }}
                >
                  {folder.name}
                </span>
                {folder.domain_status && (
                  <span className={`folder-domain-status ${folder.domain_status}`} title={`Domain: ${folder.domain_status}`}>
                    {folder.domain_status === "active" ? "🟢" : "🟡"}
                  </span>
                )}
                <span className="folder-count">{folder.conversation_count}</span>
                <button 
                  className="folder-delete-btn"
                  onClick={(e) => deleteFolder(e, folder.id)}
                  title="Delete folder"
                >
                  ×
                </button>
              </div>

              {/* Render conversations inside expanded folders */}
              {isExpanded && folderConversations.length > 0 && (
                <div className="folder-contents">
                  {folderConversations.map(conv => (
                    <ConversationItem
                      key={conv.id}
                      conversation={conv}
                      selected={conv.id === selectedId}
                      onSelect={onSelect}
                      onDragStart={handleDragStart}
                      onRename={handleRename}
                      onPin={onPinConversation}
                      onArchive={onArchiveConversation}
                      menuOpen={openMenuId === conv.id}
                      onToggleMenu={toggleMenu}
                    />
                  ))}
                </div>
              )}
              
              {/* Empty state for expanded folder with no conversations */}
              {isExpanded && folderConversations.length === 0 && (
                <div className="folder-contents folder-empty">
                  <span>Drop conversations here</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
      {/* Pinned Section - above all date groups */}
      {pinned.length > 0 && (
        <div className="pinned-section">
          <div className="section-header" onClick={() => toggleSection('pinned')}>
            <span className="section-chevron">{isSectionExpanded('pinned') ? '▼' : '▶'}</span>
            <span>PINNED</span>
          </div>
          {isSectionExpanded('pinned') && pinned.map(conv => (
            <ConversationItem
              key={conv.id}
              conversation={conv}
              selected={conv.id === selectedId}
              onSelect={onSelect}
              onDragStart={handleDragStart}
              onRename={handleRename}
              onPin={onPinConversation}
              onArchive={onArchiveConversation}
              menuOpen={openMenuId === conv.id}
              onToggleMenu={toggleMenu}
            />
          ))}
        </div>
      )}

      {/* Unfiled Section with Date Groups - only shows conversations without folder_id */}
      <div 
        className="unfiled-section"
        onDragOver={e => handleDragOver(e, null)}
        onDragLeave={handleDragLeave}
        onDrop={e => handleDrop(e, null)}
      >
        {Object.entries(groupedUnfiled).map(([label, convos]) => (
          <div key={label} className="date-group">
            <div className="section-header" onClick={() => toggleSection(label)}>
              <span className="section-chevron">{isSectionExpanded(label) ? '▼' : '▶'}</span>
              <span>{label}</span>
            </div>
            {isSectionExpanded(label) && convos.map(conv => (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                selected={conv.id === selectedId}
                onSelect={onSelect}
                onDragStart={handleDragStart}
                onRename={handleRename}
                onPin={onPinConversation}
                onArchive={onArchiveConversation}
                menuOpen={openMenuId === conv.id}
                onToggleMenu={toggleMenu}
              />
            ))}
          </div>
        ))}
        
        {/* Show hint when no unfiled conversations */}
        {Object.keys(groupedUnfiled).length === 0 && folders.length > 0 && (
          <div className="unfiled-empty">
            <span>All conversations are organized in folders</span>
          </div>
        )}
      </div>

      {/* Archived Section - collapsible, at the very bottom */}
      {archived.length > 0 && (
        <div className="archived-section">
          <div className="section-header" onClick={() => toggleSection('archived')}>
            <span className="section-chevron">{isSectionExpanded('archived') ? '▼' : '▶'}</span>
            <span>ARCHIVED ({archived.length})</span>
          </div>
          {isSectionExpanded('archived') && archived.map(conv => (
            <ConversationItem
              key={conv.id}
              conversation={conv}
              selected={conv.id === selectedId}
              onSelect={onSelect}
              onDragStart={handleDragStart}
              onRename={handleRename}
              onPin={onPinConversation}
              onArchive={onArchiveConversation}
              menuOpen={openMenuId === conv.id}
              onToggleMenu={toggleMenu}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ConversationItem({ conversation, selected, onSelect, onDragStart, onRename, onPin, onArchive, menuOpen, onToggleMenu }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  const getStatusDot = (status) => {
    const colors = {
      'active': '#10b981',
      'completed': '#6366f1',
      'abandoned': '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const formatDateTitle = (conv) => {
    const dateString = conv.created_at || conv.updated_at;
    let isoString = dateString;
    if (isoString && !isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}(:\d{2})?$/)) {
      isoString = isoString + 'Z';
    }
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDisplayTitle = (conv) => {
    return conv.title || formatDateTitle(conv);
  };

  const handleStartEdit = (e) => {
    if (e) e.stopPropagation();
    setEditTitle(conversation.title || '');
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    const newTitle = editTitle.trim();
    if (onRename) {
      await onRename(conversation.id, newTitle || null);
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditTitle('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleMenuAction = (action, e) => {
    e.stopPropagation();
    onToggleMenu(conversation.id, e);
    switch (action) {
      case 'rename':
        handleStartEdit();
        break;
      case 'pin':
        if (onPin) {
          onPin(conversation.id);
        }
        break;
      case 'archive':
        if (onArchive) {
          onArchive(conversation.id);
        }
        break;
      default:
        break;
    }
  };

  const handleClick = (e) => {
    if (!isEditing) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      setTimeout(() => {
        onSelect(conversation.id);
      }, 10);
    }
  };

  const handleToggleMenu = (id, e) => {
    e.stopPropagation();
    if (!menuOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
    onToggleMenu(id, e);
  };

  const dropdown = menuOpen ? ReactDOM.createPortal(
    <div
      className="conversation-dropdown"
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        zIndex: 9999,
      }}
      ref={menuRef}
    >
      <button onClick={(e) => handleMenuAction('rename', e)}>
        <span className="menu-icon">✏️</span> Rename
      </button>
      <button onClick={(e) => handleMenuAction('pin', e)}>
        <span className="menu-icon">📌</span> {conversation.pinned ? 'Unpin' : 'Pin'}
      </button>
      <button onClick={(e) => handleMenuAction('archive', e)}>
        <span className="menu-icon">📥</span> {conversation.archived ? 'Unarchive' : 'Archive'}
      </button>
    </div>,
    document.body
  ) : null;

  return (
    <div
      className={`conversation-item ${selected ? 'selected' : ''}`}
      onClick={handleClick}
      draggable={!isEditing}
      onDragStart={e => onDragStart(e, conversation.id)}
    >
      <span
        className="status-dot"
        style={{ backgroundColor: getStatusDot(conversation.status) }}
        title={conversation.status}
      />
      {isEditing ? (
        <input
          type="text"
          className="conversation-title-input"
          value={editTitle}
          onChange={e => setEditTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSaveEdit}
          placeholder={formatDateTitle(conversation)}
          autoFocus
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <span className="conversation-preview">
          {getDisplayTitle(conversation)}
        </span>
      )}

      {!isEditing && (
        <div className="conversation-menu-container">
          <button
            className="ellipsis-btn"
            ref={btnRef}
            onClick={handleToggleMenu.bind(null, conversation.id)}
            title="Actions"
          >
            ⋮
          </button>
          {dropdown}
        </div>
      )}
    </div>
  );
}

function groupByDate(conversations) {
  const groups = {
    'Today': [],
    'Yesterday': [],
    'Last Week': [],
    'This Month': [],
    'Older': []
  };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  conversations.forEach(conv => {
    const dateString = conv.created_at || conv.updated_at;
    // Ensure UTC interpretation: append 'Z' if no timezone indicator present
    let isoString = dateString;
    if (isoString && !isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}(:\d{2})?$/)) {
      isoString = isoString + 'Z';
    }
    const date = new Date(isoString);
    
    if (date >= today) {
      groups['Today'].push(conv);
    } else if (date >= yesterday) {
      groups['Yesterday'].push(conv);
    } else if (date >= lastWeek) {
      groups['Last Week'].push(conv);
    } else if (date >= thisMonth) {
      groups['This Month'].push(conv);
    } else {
      groups['Older'].push(conv);
    }
  });

  return Object.fromEntries(
    Object.entries(groups).filter(([_, convos]) => convos.length > 0)
  );
}
